import React, { useEffect, useState } from 'react'
import moment from 'moment'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AnDatePicker from '../../../../components/DateTime/DatePicker'
import ShiftSlotAssignments, {
  emptySlot,
  isSlotAssigned,
  SlotAssignmentFields,
} from './ShiftSlotAssignments'
import {
  formatShiftRange,
  getOccurrenceSlots,
  MAX_REPEAT_OCCURRENCES,
  MAX_REPEAT_SHIFTS,
  repeatDayOptions,
} from './shiftRepeat'

const DAY_PRESETS = [
  { label: 'Every day', days: [0, 1, 2, 3, 4, 5, 6] },
  { label: 'Weekdays', days: [1, 2, 3, 4, 5] },
  { label: 'Weekends', days: [0, 6] },
]

const LENGTH_PRESETS = [1, 2, 4, 8]

const sameDays = (a = [], b = []) =>
  a.length === b.length && a.every((day) => b.includes(day))

const SectionLabel = ({ children }) => (
  <Typography
    variant="overline"
    color="text.secondary"
    fontWeight={700}
    lineHeight={1.6}
    display="block"
  >
    {children}
  </Typography>
)

const Stat = ({ value, label, tone }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      px: 1.5,
      py: 1,
      borderRadius: 2,
      border: 1,
      borderColor: 'divider',
      bgcolor: 'background.default',
    }}
  >
    <Typography
      variant="h5"
      fontWeight={800}
      lineHeight={1.2}
      color={tone ?? 'text.primary'}
    >
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary" noWrap display="block">
      {label}
    </Typography>
  </Box>
)

const RecurrenceOptions = ({ start, eventEnd, repeat, setRepeat }) => {
  const first = moment(start).startOf('day')
  const lastEventDay = eventEnd ? moment(eventEnd).startOf('day') : null
  const showEventEnd =
    lastEventDay?.isValid() && lastEventDay.isAfter(first, 'day')
  const lengthWeeks = (weeks) =>
    first.clone().add(weeks, 'weeks').subtract(1, 'day')

  const toggleDay = (value) =>
    setRepeat((prev) => {
      const days = new Set(prev.daysOfWeek ?? [])
      if (days.has(value)) days.delete(value)
      else days.add(value)
      return { ...prev, daysOfWeek: [...days].sort() }
    })

  const untilChip = (label, until) => {
    const selected = moment(repeat.until).isSame(until, 'day')
    return (
      <Chip
        key={label}
        size="small"
        clickable
        label={label}
        color={selected ? 'primary' : 'default'}
        variant={selected ? 'filled' : 'outlined'}
        onClick={() => setRepeat((prev) => ({ ...prev, until }))}
      />
    )
  }

  return (
    <Stack spacing={2.5}>
      <Box>
        <SectionLabel>Repeat on</SectionLabel>
        <Stack direction="row" gap={0.75} flexWrap="wrap" mt={0.5}>
          {repeatDayOptions.map((day) => {
            const checked = repeat.daysOfWeek?.includes(day.value)
            return (
              <Tooltip key={day.value} title={day.tooltip}>
                <Button
                  size="small"
                  variant={checked ? 'contained' : 'outlined'}
                  onClick={() => toggleDay(day.value)}
                  aria-pressed={checked}
                  aria-label={day.tooltip}
                  sx={{
                    minWidth: 36,
                    width: 36,
                    height: 36,
                    px: 0,
                    borderRadius: '50%',
                  }}
                >
                  {day.label}
                </Button>
              </Tooltip>
            )
          })}
        </Stack>
        <Stack direction="row" gap={0.75} flexWrap="wrap" mt={1}>
          {DAY_PRESETS.map((preset) => {
            const selected = sameDays(repeat.daysOfWeek, preset.days)
            return (
              <Chip
                key={preset.label}
                size="small"
                label={preset.label}
                clickable
                color={selected ? 'primary' : 'default'}
                variant={selected ? 'filled' : 'outlined'}
                onClick={() =>
                  setRepeat((prev) => ({
                    ...prev,
                    daysOfWeek: [...preset.days].sort(),
                  }))
                }
              />
            )
          })}
        </Stack>
      </Box>

      <TextField
        select
        size="small"
        label="Frequency"
        value={repeat.intervalWeeks ?? 1}
        onChange={(e) =>
          setRepeat((prev) => ({
            ...prev,
            intervalWeeks: Number(e.target.value),
          }))
        }
        fullWidth
      >
        <MenuItem value={1}>Every week</MenuItem>
        <MenuItem value={2}>Every 2 weeks</MenuItem>
        <MenuItem value={3}>Every 3 weeks</MenuItem>
        <MenuItem value={4}>Every 4 weeks</MenuItem>
      </TextField>

      <Box>
        <SectionLabel>For how long</SectionLabel>
        <Stack direction="row" gap={0.75} flexWrap="wrap" mt={0.5} mb={1.5}>
          {showEventEnd && untilChip('Until the event ends', lastEventDay)}
          {LENGTH_PRESETS.map((weeks) =>
            untilChip(
              `${weeks} week${weeks === 1 ? '' : 's'}`,
              lengthWeeks(weeks),
            ),
          )}
        </Stack>
        <AnDatePicker
          label="Repeat until"
          date={repeat.until}
          openTo="day"
          minDate={first}
          onChange={(value) =>
            setRepeat((prev) => ({
              ...prev,
              until:
                value && moment(value).isValid()
                  ? moment(value).startOf('day')
                  : null,
            }))
          }
          size="small"
          fullWidth
        />
      </Box>

      <Typography variant="caption" color="text.secondary">
        Every day gets the same times, role, pay and notes as the shift above.
        The run starts on{' '}
        <b>{first.isValid() ? first.format('ddd D MMM') : '…'}</b>; days that
        aren&apos;t selected are skipped.
      </Typography>
    </Stack>
  )
}

const OccurrenceRow = ({
  occurrence,
  occurrenceIndex,
  slots,
  sameStaff,
  expanded,
  onToggle,
  onSlotsChange,
  issuesBySlot,
  onClearIssues,
}) => {
  const assignedCount = slots.filter(isSlotAssigned).length
  const needsAttention = Object.keys(issuesBySlot ?? {}).length > 0

  return (
    <Accordion
      expanded={expanded}
      onChange={(e, open) => onToggle(open)}
      disableGutters
      elevation={0}
      TransitionProps={{ unmountOnExit: true }}
      sx={{
        border: 1,
        borderRadius: 2,
        boxShadow: 'none',
        borderColor: needsAttention
          ? 'error.main'
          : expanded
            ? 'primary.main'
            : 'divider',
        bgcolor: 'background.paper',
        '&:not(:last-child)': { mb: 1 },
        '&::before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon fontSize="small" />}
        sx={{
          px: 1.5,
          minHeight: 56,
          '& .MuiAccordionSummary-content': {
            my: 1,
            alignItems: 'center',
            gap: 1.5,
            minWidth: 0,
          },
        }}
      >
        <Box
          sx={{
            width: 44,
            flexShrink: 0,
            textAlign: 'center',
            borderRadius: 1.5,
            py: 0.25,
            bgcolor: 'background.default',
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            lineHeight={1.3}
            fontWeight={700}
          >
            {occurrence.start.format('ddd').toUpperCase()}
          </Typography>
          <Typography fontWeight={800} lineHeight={1.1}>
            {occurrence.start.format('D')}
          </Typography>
        </Box>
        <Box minWidth={0} flex={1}>
          <Typography variant="body2" fontWeight={700} noWrap component="div">
            {occurrence.start.format('D MMMM YYYY')}
            {occurrenceIndex === 0 && (
              <Chip
                size="small"
                label="First"
                sx={{ ml: 1, height: 18, fontSize: 11 }}
              />
            )}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {formatShiftRange(occurrence.start, occurrence.end)} ·{' '}
            {assignedCount} of {slots.length} assigned
          </Typography>
        </Box>
        {needsAttention && (
          <Chip size="small" color="error" label="Needs attention" />
        )}
      </AccordionSummary>
      <AccordionDetails
        sx={{ px: 1.5, pt: 0.5, pb: 1.75, bgcolor: 'background.default' }}
      >
        {slots.length > 1 ? (
          <ShiftSlotAssignments
            slots={slots}
            onChange={onSlotsChange}
            disabled={sameStaff}
            issuesBySlot={issuesBySlot}
            onClearIssues={onClearIssues}
          />
        ) : (
          <SlotAssignmentFields
            slot={slots[0]}
            dense
            disabled={sameStaff}
            issues={issuesBySlot?.[0]}
            onClearIssues={() => onClearIssues(0)}
            onAssigneeChange={(e, value) =>
              onSlotsChange([
                { assignedTo: value ?? null, assignedToSubcontractor: false },
              ])
            }
            onSubcontractorToggle={(checked) =>
              onSlotsChange([
                { assignedTo: null, assignedToSubcontractor: checked },
              ])
            }
          />
        )}
      </AccordionDetails>
    </Accordion>
  )
}

const RepeatSummary = ({
  repeat,
  setRepeat,
  occurrences,
  slots,
  quantity,
  issuesByOccurrence,
  onClearIssues,
}) => {
  const [expanded, setExpanded] = useState(null)
  const overCap = occurrences.length > MAX_REPEAT_OCCURRENCES
  const shiftCount = occurrences.length * quantity
  const slotsByOccurrence = occurrences.map((occurrence, occurrenceIndex) =>
    getOccurrenceSlots(repeat, occurrence, occurrenceIndex, slots, quantity),
  )
  const assignedCount = slotsByOccurrence.flat().filter(isSlotAssigned).length
  const openCount = shiftCount - assignedCount
  const anyPicked = slots.some(isSlotAssigned)

  // Open the first day the server rejected so the reason is visible
  useEffect(() => {
    const flagged = Object.keys(issuesByOccurrence ?? {}).map(Number)
    if (flagged.length) setExpanded(Math.min(...flagged))
  }, [issuesByOccurrence])

  const setDaySlots = (occurrenceKey, next) =>
    setRepeat((prev) => ({
      ...prev,
      assignments: { ...prev.assignments, [occurrenceKey]: next },
    }))

  // Turning "same staff" off keeps what each day currently shows, so nothing
  // visibly changes until the user edits a day
  const toggleSameStaff = (checked) =>
    setRepeat((prev) => {
      if (checked) return { ...prev, sameStaff: true }
      const assignments = {}
      occurrences.forEach((occurrence, occurrenceIndex) => {
        assignments[occurrence.key] = getOccurrenceSlots(
          prev,
          occurrence,
          occurrenceIndex,
          slots,
          quantity,
        )
      })
      return { ...prev, sameStaff: false, assignments }
    })

  const clearOthers = () =>
    setRepeat((prev) => {
      const assignments = {}
      occurrences.forEach((occurrence, occurrenceIndex) => {
        assignments[occurrence.key] =
          occurrenceIndex === 0
            ? getOccurrenceSlots(prev, occurrence, 0, slots, quantity)
            : Array.from({ length: quantity }, emptySlot)
      })
      return { ...prev, assignments }
    })

  return (
    <Stack spacing={2} minWidth={0}>
      <Box>
        <SectionLabel>Summary</SectionLabel>
        <Stack direction="row" gap={1} mt={0.5}>
          <Stat
            value={overCap ? `${MAX_REPEAT_OCCURRENCES}+` : occurrences.length}
            label={occurrences.length === 1 ? 'day' : 'days'}
          />
          <Stat
            value={overCap ? '—' : shiftCount}
            label={shiftCount === 1 ? 'shift' : 'shifts'}
          />
          <Stat
            value={overCap ? '—' : assignedCount}
            label="assigned"
            tone={assignedCount ? 'primary.main' : undefined}
          />
          <Stat
            value={overCap ? '—' : openCount}
            label={openCount === 1 ? 'open vacancy' : 'open vacancies'}
            tone={openCount ? 'warning.main' : undefined}
          />
        </Stack>
        {occurrences.length > 0 && !overCap && (
          <Typography variant="body2" color="text.secondary" mt={1}>
            {occurrences[0].start.format('ddd D MMM')} –{' '}
            {occurrences[occurrences.length - 1].start.format('ddd D MMM YYYY')}
            {openCount > 0 && ' · open vacancies are advertised to staff'}
          </Typography>
        )}
      </Box>

      {overCap ? (
        <Alert severity="error">
          That pattern covers more than {MAX_REPEAT_OCCURRENCES} days. Shorten
          the run or pick fewer days.
        </Alert>
      ) : shiftCount > MAX_REPEAT_SHIFTS ? (
        <Alert severity="error">
          That would create {shiftCount} shifts — the most one repeat can create
          is {MAX_REPEAT_SHIFTS}. Shorten the run, pick fewer days or lower the
          number of shifts.
        </Alert>
      ) : !occurrences.length ? (
        <Alert severity="warning">
          Choose at least one day and an end date on or after the shift&apos;s
          start.
        </Alert>
      ) : null}

      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          borderRadius: 2,
          borderColor: repeat.sameStaff ? 'primary.main' : 'divider',
        }}
      >
        <FormControlLabel
          sx={{ mx: 0, alignItems: 'flex-start' }}
          control={
            <Switch
              checked={!!repeat.sameStaff}
              onChange={(e) => toggleSameStaff(e.target.checked)}
              size="small"
            />
          }
          label={
            <Box ml={0.5}>
              <Typography variant="body2" fontWeight={700}>
                Assign the same staff every day
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {repeat.sameStaff
                  ? anyPicked
                    ? 'Each day gets the staff picked in Assignment above. Turn off to assign days one by one.'
                    : 'No one is picked in Assignment above, so every shift will be an open vacancy. Turn off to assign days one by one.'
                  : 'Assign each day below. Shifts left empty are advertised as open vacancies.'}
              </Typography>
            </Box>
          }
        />
        {!repeat.sameStaff && occurrences.length > 1 && (
          <Box textAlign="right">
            <Button size="small" color="inherit" onClick={clearOthers}>
              Clear all but the first day
            </Button>
          </Box>
        )}
      </Paper>

      {occurrences.length > 0 && !overCap && (
        <Box
          sx={{
            maxHeight: { lg: 460 },
            overflowY: { lg: 'auto' },
            pr: { lg: 0.5 },
          }}
        >
          {occurrences.map((occurrence, occurrenceIndex) => (
            <OccurrenceRow
              key={occurrence.key}
              occurrence={occurrence}
              occurrenceIndex={occurrenceIndex}
              slots={slotsByOccurrence[occurrenceIndex]}
              sameStaff={repeat.sameStaff}
              expanded={expanded === occurrenceIndex}
              onToggle={(open) => setExpanded(open ? occurrenceIndex : null)}
              onSlotsChange={(next) => setDaySlots(occurrence.key, next)}
              issuesBySlot={issuesByOccurrence?.[occurrenceIndex]}
              onClearIssues={(slotIndex) =>
                onClearIssues(occurrenceIndex, slotIndex)
              }
            />
          ))}
        </Box>
      )}
    </Stack>
  )
}

// Recurrence options on the left, the days it creates (and who's on each) on
// the right; stacked below lg.
const RepeatShiftPanel = ({ start, eventEnd, ...props }) => (
  <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
    {/* Box keeps the Grid gutter intact inside the Section's Stack */}
    <Box>
      <Grid container>
        <Grid
          item
          xs={12}
          lg={5}
          sx={{
            p: 2,
            bgcolor: 'background.default',
            borderRight: { lg: 1 },
            borderBottom: { xs: 1, lg: 0 },
            borderColor: { xs: 'divider', lg: 'divider' },
          }}
        >
          <RecurrenceOptions
            start={start}
            eventEnd={eventEnd}
            repeat={props.repeat}
            setRepeat={props.setRepeat}
          />
        </Grid>
        <Grid item xs={12} lg={7} sx={{ p: 2, minWidth: 0 }}>
          <RepeatSummary {...props} />
        </Grid>
      </Grid>
    </Box>
  </Paper>
)

export default RepeatShiftPanel
